import React, { useState, useEffect } from 'react';
import { GraduationCap, Download, Calendar, MapPin, Award, Clock, FileText, ChevronDown, ChevronUp, X, BookOpen, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import RegistrationModal from '../components/RegistrationModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CMEs = () => {
  const [showPrevious, setShowPrevious] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [isPreviousCoursesModalOpen, setIsPreviousCoursesModalOpen] = useState(false);
  const [isCurrentCoursesModalOpen, setIsCurrentCoursesModalOpen] = useState(false);
  const [isFutureCoursesModalOpen, setIsFutureCoursesModalOpen] = useState(false);
  const [courseDetailModal, setCourseDetailModal] = useState({ isOpen: false, course: null });
  const [selectedCurrentCourseId, setSelectedCurrentCourseId] = useState(null);
  const [cmeCourses, setCmeCourses] = useState({ previous: [], active: [], future: [] });
  const [loading, setLoading] = useState(true);
  const { t, isRTL } = useLanguage();

  // Fetch courses from API
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/courses`);
        setCmeCourses(response.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const handleRegisterClick = (courseName) => {
    setSelectedCourse(courseName);
    setIsModalOpen(true);
  };

  const openCourseDetail = (course) => {
    setCourseDetailModal({ isOpen: true, course });
  };

  const closeCourseDetail = () => {
    setCourseDetailModal({ isOpen: false, course: null });
  };

  const handleCurrentCourseSelection = (courseId) => {
    setIsCurrentCoursesModalOpen(false);
    setSelectedCurrentCourseId(courseId);
    // Scroll to the course display area
    setTimeout(() => {
      const courseSection = document.getElementById('current-courses-display');
      if (courseSection) {
        courseSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Filter courses to display
  const coursesToDisplay = selectedCurrentCourseId 
    ? cmeCourses.active.filter(course => course.id === selectedCurrentCourseId)
    : cmeCourses.active;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div 
        className="relative h-80 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/5998477/pexels-photo-5998477.jpeg?w=1920&q=80)' }}
      >
        <div className="absolute inset-0 bg-slate-900 bg-opacity-60 flex items-center justify-center">
          {/* Logo Watermark */}
          <img 
            src="/logo.png" 
            alt="Target Health" 
            className="absolute bottom-6 left-6 w-40 opacity-20"
          />
          <h1 className="text-5xl font-bold text-white text-center px-4 z-10">{t.cme.title}</h1>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-2">
              {isRTL ? t.cme.mainTitle : 'CONTINUING MEDICAL'} <span className="text-cyan-500">{isRTL ? '' : 'EDUCATION'}</span>
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-4"></div>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t.cme.subtitle}
            </p>
          </div>

          {/* Previous, Current and Future Courses Boxes */}
          <div className="max-w-6xl mx-auto mb-12 grid md:grid-cols-3 gap-6">
            {/* Previous Courses Box */}
            <button
              onClick={() => setIsPreviousCoursesModalOpen(true)}
              className="group bg-gradient-to-br from-slate-100 to-slate-200 hover:from-cyan-50 hover:to-cyan-100 rounded-xl p-8 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <div className={`flex flex-col items-center gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="bg-slate-600 group-hover:bg-cyan-500 rounded-full p-6 transition-colors">
                  <BookOpen size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 group-hover:text-cyan-600">
                  {t.cme.previousCourses}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isRTL ? 'عرض أرشيف الدورات المنجزة' : 'View archive of completed courses'}
                </p>
                <span className="bg-slate-600 group-hover:bg-cyan-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {cmeCourses.previous.length} {isRTL ? 'دورات' : 'Courses'}
                </span>
              </div>
            </button>

            {/* Current Courses Box */}
            <button
              onClick={() => setIsCurrentCoursesModalOpen(true)}
              className="group bg-gradient-to-br from-cyan-100 to-cyan-200 hover:from-cyan-200 hover:to-cyan-300 rounded-xl p-8 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <div className={`flex flex-col items-center gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="bg-cyan-500 group-hover:bg-cyan-600 rounded-full p-6 transition-colors">
                  <GraduationCap size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 group-hover:text-cyan-700">
                  {t.cme.currentCourses}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isRTL ? 'عرض الدورات الجارية' : 'View ongoing training programs'}
                </p>
                <span className="bg-cyan-500 group-hover:bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {cmeCourses.active.length} {isRTL ? 'برامج' : 'Programs'}
                </span>
              </div>
            </button>

            {/* Future Courses Box */}
            <button
              onClick={() => setIsFutureCoursesModalOpen(true)}
              className="group bg-gradient-to-br from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 rounded-xl p-8 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <div className={`flex flex-col items-center gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="bg-orange-500 group-hover:bg-orange-600 rounded-full p-6 transition-colors">
                  <Star size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 group-hover:text-orange-600">
                  {t.cme.futureCourses}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isRTL ? 'اكتشف البرامج القادمة' : 'Discover upcoming programs'}
                </p>
                <span className="bg-orange-500 group-hover:bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {cmeCourses.future.length} {isRTL ? 'برامج' : 'Programs'}
                </span>
              </div>
            </button>
          </div>

          {/* Active Courses */}
          {coursesToDisplay.length > 0 ? (
            <div id="current-courses-display" className="max-w-6xl mx-auto mb-16">
              <h3 className="text-3xl font-bold text-slate-800 mb-8 text-center">
                {isRTL ? t.cme.currentPrograms : 'Current'} <span className="text-cyan-500">{isRTL ? '' : 'Training Programs'}</span>
              </h3>
              
              {coursesToDisplay.map((course) => (
                <div key={course.id} className="bg-white rounded-lg shadow-xl overflow-hidden mb-8">
                  <div className="grid md:grid-cols-2 gap-8 p-8">
                    {/* Left Column - Video */}
                    <div>
                      <div className="bg-slate-900 rounded-lg overflow-hidden mb-4 relative">
                        <video 
                          controls
                          className="w-full h-auto"
                        >
                          <source src={course.videoUrl} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>

                      {/* Course Details */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-cyan-50 p-4 rounded-lg">
                          <div className={`flex items-center gap-2 text-cyan-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Clock size={20} />
                            <span className="font-semibold">{t.cme.duration}</span>
                          </div>
                          <p className={`text-slate-700 ${isRTL ? 'text-right' : ''}`}>
                            {isRTL && course.durationArabic ? course.durationArabic : course.duration}
                          </p>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg">
                          <div className={`flex items-center gap-2 text-orange-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Award size={20} />
                            <span className="font-semibold">{t.cme.certificate}</span>
                          </div>
                          <p className={`text-slate-700 ${isRTL ? 'text-right' : ''}`}>
                            {isRTL && course.creditsArabic ? course.creditsArabic : course.credits}
                          </p>
                        </div>
                        {course.startDate && (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className={`flex items-center gap-2 text-blue-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Calendar size={20} />
                              <span className="font-semibold">{t.cme.startDate}</span>
                            </div>
                            <p className="text-slate-700 text-sm">{course.startDate}</p>
                          </div>
                        )}
                        <div className="bg-green-50 p-4 rounded-lg">
                          <div className={`flex items-center gap-2 text-green-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <MapPin size={20} />
                            <span className="font-semibold">{t.cme.location}</span>
                          </div>
                          <p className={`text-slate-700 text-sm ${isRTL ? 'text-right' : ''}`}>
                            {isRTL && course.locationArabic ? course.locationArabic : course.location}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Course Info */}
                    <div>
                      <div className="mb-6">
                        <span className="inline-block bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                          {course.category}
                        </span>
                        <h4 className="text-3xl font-bold text-slate-800 mb-3">
                          {isRTL && course.titleArabic ? course.titleArabic : course.title}
                        </h4>
                        {!isRTL && course.titleArabic && (
                          <h5 className="text-2xl font-bold text-slate-600 mb-4 text-right" dir="rtl">
                            {course.titleArabic}
                          </h5>
                        )}
                      </div>

                      <div className="mb-6">
                        <p className={`text-slate-700 leading-relaxed mb-4 ${isRTL ? 'text-right' : ''}`} style={{ whiteSpace: 'pre-line' }}>
                          {isRTL && course.descriptionArabic ? course.descriptionArabic : course.description}
                        </p>
                      </div>

                      {/* Download Brochures */}
                      <div className="mb-6">
                        <h5 className={`font-bold text-slate-800 mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <FileText size={20} className="text-cyan-500" />
                          {t.cme.downloadMaterials}
                        </h5>
                        <div className="space-y-2">
                          {course.brochures.map((brochure, index) => (
                            <a
                              key={index}
                              href={brochure.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors group ${isRTL ? 'flex-row-reverse' : ''}`}
                            >
                              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <div className="bg-red-500 p-2 rounded">
                                  <Download size={20} className="text-white" />
                                </div>
                                <span className="font-semibold text-slate-700 group-hover:text-cyan-600">
                                  {brochure.title}
                                </span>
                              </div>
                              <span className="text-slate-400 text-sm">PDF</span>
                            </a>
                          ))}
                        </div>
                      </div>

                      {/* Register Button */}
                      <button
                        onClick={() => handleRegisterClick(isRTL && course.titleArabic ? course.titleArabic : course.title)}
                        className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-center px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg"
                      >
                        🎓 {t.cme.registerHere}
                      </button>
                      <p className="text-center text-slate-500 text-sm mt-2">
                        {t.cme.limitedSeats}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="max-w-5xl mx-auto text-center py-20">
              <div className="bg-slate-50 rounded-lg p-16">
                <GraduationCap size={64} className="text-slate-300 mx-auto mb-4" />
                <h3 className="text-6xl font-bold text-slate-300 mb-4">{t.events.comingSoon}</h3>
                <p className="text-slate-600 text-lg">
                  {isRTL ? 'سيتم الإعلان عن برامج تدريبية جديدة قريباً. ترقبوا التحديثات!' : 'New training programs will be announced soon. Stay tuned for updates!'}
                </p>
              </div>
            </div>
          )}

          {/* Why Choose Our Training */}
          <div className="mt-16 bg-gradient-to-br from-slate-50 to-cyan-50 p-8 rounded-lg">
            <h3 className="text-3xl font-bold text-slate-800 mb-8 text-center">
              {isRTL ? t.cme.whyChoose : 'Why Choose Our'} <span className="text-cyan-500">{isRTL ? '' : 'Training Programs?'}</span>
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-white w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full shadow-md">
                  <Award size={32} className="text-cyan-500" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">{t.cme.accreditedPrograms}</h4>
                <p className="text-slate-600 text-sm">{t.cme.accreditedProgramsDesc}</p>
              </div>
              <div className="text-center">
                <div className="bg-white w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full shadow-md">
                  <GraduationCap size={32} className="text-cyan-500" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">{t.cme.expertInstructors}</h4>
                <p className="text-slate-600 text-sm">{t.cme.expertInstructorsDesc}</p>
              </div>
              <div className="text-center">
                <div className="bg-white w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full shadow-md">
                  <FileText size={32} className="text-cyan-500" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">{t.cme.comprehensiveMaterials}</h4>
                <p className="text-slate-600 text-sm">{t.cme.comprehensiveMaterialsDesc}</p>
              </div>
              <div className="text-center">
                <div className="bg-white w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full shadow-md">
                  <Clock size={32} className="text-cyan-500" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">{t.cme.flexibleScheduling}</h4>
                <p className="text-slate-600 text-sm">{t.cme.flexibleSchedulingDesc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Previous Courses Modal */}
      {isPreviousCoursesModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setIsPreviousCoursesModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-slate-600 text-white p-6 rounded-t-lg flex items-center justify-between flex-shrink-0">
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <BookOpen size={28} />
                <h3 className="text-2xl font-bold">{t.cme.previousCourses}</h3>
              </div>
              <button
                onClick={() => setIsPreviousCoursesModalOpen(false)}
                className="hover:bg-slate-700 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Course List */}
            <div className="overflow-y-auto p-6 flex-1">
              <div className="grid md:grid-cols-2 gap-6">
                {cmeCourses.previous.map((course) => (
                  <div 
                    key={course.id}
                    className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group"
                    onClick={() => {
                      setIsPreviousCoursesModalOpen(false);
                      openCourseDetail(course);
                    }}
                  >
                    {course.image && (
                      <div className="w-full h-48 bg-slate-100 flex items-center justify-center overflow-hidden rounded-t-lg">
                        <img 
                          src={course.image} 
                          alt={course.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="inline-block bg-slate-200 text-slate-600 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                        {t.cme.completed}
                      </span>
                      <h4 className={`text-lg font-bold text-slate-800 mb-2 group-hover:text-cyan-600 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && course.titleArabic ? course.titleArabic : course.title}
                      </h4>
                      <p className={`text-slate-600 text-sm mb-3 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && course.descriptionArabic ? course.descriptionArabic : course.description}
                      </p>
                      <p className={`text-slate-500 text-xs mb-4 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && course.notesArabic ? course.notesArabic : course.notes}
                      </p>
                      <div className={`flex items-center gap-2 text-sm text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Calendar size={14} />
                        <span>{isRTL && course.completedDateArabic ? course.completedDateArabic : course.completedDate}</span>
                      </div>
                      <div className="mt-3 text-cyan-500 group-hover:text-cyan-600 font-semibold text-sm">
                        {t.cme.viewDetails} →
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current Courses Modal */}
      {isCurrentCoursesModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setIsCurrentCoursesModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-cyan-500 text-white p-6 rounded-t-lg flex items-center justify-between flex-shrink-0">
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <GraduationCap size={28} />
                <h3 className="text-2xl font-bold">{t.cme.currentCourses}</h3>
              </div>
              <button
                onClick={() => setIsCurrentCoursesModalOpen(false)}
                className="hover:bg-cyan-600 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Course List */}
            <div className="overflow-y-auto p-6 flex-1">
              <div className="grid md:grid-cols-2 gap-6">
                {cmeCourses.active.map((course) => (
                  <div 
                    key={course.id}
                    className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group"
                    onClick={() => handleCurrentCourseSelection(course.id)}
                  >
                    {course.image ? (
                      <img 
                        src={course.image} 
                        alt={course.title}
                        className="w-full h-40 object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                        <GraduationCap size={64} className="text-white opacity-50" />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="inline-block bg-cyan-500 text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
                        {isRTL ? 'نشط الآن' : 'Active Now'}
                      </span>
                      <h4 className={`text-lg font-bold text-slate-800 mb-2 group-hover:text-cyan-600 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && course.titleArabic ? course.titleArabic : course.title}
                      </h4>
                      <p className={`text-slate-600 text-sm mb-3 line-clamp-3 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && course.descriptionArabic ? course.descriptionArabic.substring(0, 150) + '...' : course.description.substring(0, 150) + '...'}
                      </p>
                      <div className={`flex items-center gap-4 text-xs text-slate-500 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Clock size={14} />
                          <span>{isRTL && course.durationArabic ? course.durationArabic : course.duration}</span>
                        </div>
                        <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <MapPin size={14} />
                          <span>{isRTL && course.locationArabic ? course.locationArabic : course.location}</span>
                        </div>
                      </div>
                      <div className="mt-3 text-cyan-500 group-hover:text-cyan-600 font-semibold text-sm">
                        {isRTL ? 'اختر هذه الدورة →' : 'Select This Course →'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Future Courses Modal */}
      {isFutureCoursesModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setIsFutureCoursesModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-orange-500 text-white p-6 rounded-t-lg flex items-center justify-between flex-shrink-0">
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Star size={28} />
                <h3 className="text-2xl font-bold">{t.cme.futureCourses}</h3>
              </div>
              <button
                onClick={() => setIsFutureCoursesModalOpen(false)}
                className="hover:bg-orange-600 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Course List */}
            <div className="overflow-y-auto p-6 flex-1">
              <div className="grid md:grid-cols-2 gap-6">
                {cmeCourses.future.map((course) => (
                  <div 
                    key={course.id}
                    className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group"
                    onClick={() => {
                      setIsFutureCoursesModalOpen(false);
                      openCourseDetail(course);
                    }}
                  >
                    {course.image && (
                      <div className="w-full h-48 bg-slate-100 flex items-center justify-center overflow-hidden rounded-t-lg">
                        <img 
                          src={course.image} 
                          alt={course.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="inline-block bg-orange-200 text-orange-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                        {t.cme.planned}
                      </span>
                      <h4 className={`text-lg font-bold text-slate-800 mb-2 group-hover:text-orange-600 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && course.titleArabic ? course.titleArabic : course.title}
                      </h4>
                      <p className={`text-slate-600 text-sm mb-3 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && course.descriptionArabic ? course.descriptionArabic : course.description}
                      </p>
                      <p className={`text-slate-500 text-xs mb-4 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && course.notesArabic ? course.notesArabic : course.notes}
                      </p>
                      <div className={`flex items-center gap-2 text-sm text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Calendar size={14} />
                        <span>{isRTL && course.plannedDateArabic ? course.plannedDateArabic : course.plannedDate}</span>
                      </div>
                      <div className="mt-3 text-orange-500 group-hover:text-orange-600 font-semibold text-sm">
                        {t.cme.viewDetails} →
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Detail Modal */}
      {courseDetailModal.isOpen && courseDetailModal.course && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={closeCourseDetail}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Image */}
            <div className="relative flex-shrink-0 bg-slate-100">
              <div className="w-full h-80 flex items-center justify-center">
                <img 
                  src={courseDetailModal.course.image} 
                  alt={courseDetailModal.course.title}
                  className="max-w-full max-h-80 object-contain rounded-t-lg"
                />
              </div>
              <button
                onClick={closeCourseDetail}
                className="absolute top-4 right-4 bg-white hover:bg-slate-100 rounded-full p-2 shadow-lg transition-colors"
              >
                <X size={24} className="text-slate-800" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto p-8 flex-1">
              <div className="mb-4">
                <span className="inline-block bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                  {courseDetailModal.course.category}
                </span>
              </div>
              
              <h2 className={`text-3xl font-bold text-slate-800 mb-4 ${isRTL ? 'text-right' : ''}`}>
                {isRTL && courseDetailModal.course.titleArabic ? courseDetailModal.course.titleArabic : courseDetailModal.course.title}
              </h2>

              <p className={`text-slate-700 text-lg mb-6 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                {isRTL && courseDetailModal.course.descriptionArabic ? courseDetailModal.course.descriptionArabic : courseDetailModal.course.description}
              </p>

              {/* Course Details Grid */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-cyan-50 p-4 rounded-lg">
                  <div className={`flex items-center gap-2 text-cyan-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Clock size={20} />
                    <span className="font-semibold">{t.cme.duration}</span>
                  </div>
                  <p className={`text-slate-700 ${isRTL ? 'text-right' : ''}`}>
                    {isRTL && courseDetailModal.course.durationArabic ? courseDetailModal.course.durationArabic : courseDetailModal.course.duration}
                  </p>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className={`flex items-center gap-2 text-orange-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Award size={20} />
                    <span className="font-semibold">{t.cme.certificate}</span>
                  </div>
                  <p className={`text-slate-700 ${isRTL ? 'text-right' : ''}`}>
                    {isRTL && courseDetailModal.course.creditsArabic ? courseDetailModal.course.creditsArabic : courseDetailModal.course.credits}
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <div className={`flex items-center gap-2 text-green-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <MapPin size={20} />
                    <span className="font-semibold">{t.cme.location}</span>
                  </div>
                  <p className={`text-slate-700 ${isRTL ? 'text-right' : ''}`}>
                    {isRTL && courseDetailModal.course.locationArabic ? courseDetailModal.course.locationArabic : courseDetailModal.course.location}
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className={`flex items-center gap-2 text-blue-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Calendar size={20} />
                    <span className="font-semibold">
                      {courseDetailModal.course.completedDate ? t.cme.completedOn : t.cme.plannedFor}
                    </span>
                  </div>
                  <p className={`text-slate-700 ${isRTL ? 'text-right' : ''}`}>
                    {courseDetailModal.course.completedDate 
                      ? (isRTL && courseDetailModal.course.completedDateArabic ? courseDetailModal.course.completedDateArabic : courseDetailModal.course.completedDate)
                      : (isRTL && courseDetailModal.course.plannedDateArabic ? courseDetailModal.course.plannedDateArabic : courseDetailModal.course.plannedDate)
                    }
                  </p>
                </div>
              </div>

              {/* Notes Section */}
              <div className="bg-slate-50 p-6 rounded-lg mb-6">
                <h4 className={`font-bold text-slate-800 mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText size={20} className="text-cyan-500" />
                  {t.cme.courseNotes}
                </h4>
                <p className={`text-slate-700 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                  {isRTL && courseDetailModal.course.notesArabic ? courseDetailModal.course.notesArabic : courseDetailModal.course.notes}
                </p>
              </div>

              {/* Close Button */}
              <button
                onClick={closeCourseDetail}
                className="w-full bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                {t.cme.closeDetails}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Modal */}
      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        courseName={selectedCourse}
      />
    </div>
  );
};

export default CMEs;
